import {AfterViewInit, ChangeDetectorRef, Component, HostListener, NgZone, OnInit} from '@angular/core';
import { Router } from '@angular/router';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { StorageUtil } from 'src/app/utils/storage.util';
import { MarinaTermUseService } from 'src/app/services/marina-term-use.service';
import { RoleService } from 'src/app/services/role.service';
import { ToastService } from 'src/app/services/toast.service';
import { TermUseType } from 'src/app/models/enums/term-use-type';
import html2pdf from 'html2pdf.js';

@Component({
  selector: 'app-default',
  templateUrl: './default.component.html',
  styleUrls: ['./default.component.scss']
})
export class DefaultComponent implements OnInit {

  private static readonly WHATSAPP_SUPPORT_PHONE = '5548991250397';
  private static readonly WHATSAPP_SUPPORT_MESSAGE =
    'Olá! Preciso de ajuda com o sistema Easymarine.';

  sidebar = true;
  animate = false;
  dock = true;
  close = false;
  animated = false;

  showTermsDialog = false;
  acceptingTerms = false;
  termsChecked = false;
  termsDocUrl = '';
  termsContent = '';
  termsVersion: number = null;
  termsRequireAcceptance = false;

  @HostListener('window:resize', ['$event']) onScroll($event: Event): void {
    this.adjustWidth();
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private marinaTermUseService: MarinaTermUseService,
    private roleService: RoleService,
    private toasty: ToastService,
    private router: Router
  ) {

  }

  ngOnInit(): void {
    this.adjustWidth();
    this.checkMarinaTermsAcceptance();
    // Estado inicial vem da preferência do usuário (EAS-249); o broadcast atualiza nas trocas.
    this.sidebar = this.resolveInitialSidebar();
    BroadcastService.get('showHideSidenav').subscribe(
      (event) => {
        this.sidebar = event;
        if (event) {
          this.animate = true;
          setTimeout(() => {
            this.animate = false;
          }, 1000);
        } else {
          this.close = true;
          setTimeout(() => {
            this.close = false;
          }, 1000);
        }
      }
    );
  }

  // Responsivo: no mobile a barra não fica docada (60px); o estado aberto/recolhido
  // é controlado pelo botão de toggle (broadcast), não mais pela largura nem pelo hover.
  adjustWidth(): void {
    if (window.outerWidth <= 992) {
      this.dock = false;
      this.animated = true;
    } else {
      this.animated = false;
      this.dock = true;
    }
  }

  // Primeiro acesso: aberto (desktop). No mobile inicia recolhido (responsivo).
  private resolveInitialSidebar(): boolean {
    if (window.outerWidth <= 992) {
      return false;
    }
    const pref = StorageUtil.getSidenavOpened();
    return pref ?? true;
  }

  // Aceite dos Termos de Uso da Marina: no acesso, se o usuário é admin e a marina ainda
  // não aceitou o termo comercial vigente, exibe o modal bloqueante até o aceite. Não-admin passa.
  private checkMarinaTermsAcceptance(): void {
    const userId = StorageUtil.getUserId();
    if (!userId) {
      return;
    }
    this.roleService.currentUserIsAdministrator(userId).subscribe({
      next: (isAdmin) => {
        if (!isAdmin) {
          return;
        }
        this.marinaTermUseService.getStatus(TermUseType.MARINA_COMMERCIAL_TERMS).subscribe({
          next: (status: any) => {
            // há termo vigente e a marina ainda não aceitou essa versão -> mostra a modal.
            // O flag "Exigir novo aceite" muda só o botão de sair: Recusar (desloga) x Cancelar (só fecha).
            if (status && status.currentVersion != null && !status.accepted) {
              this.termsDocUrl = status.documentUrl;
              this.termsContent = status.content;
              this.termsVersion = status.currentVersion;
              this.termsRequireAcceptance = status.requireAcceptance;
              this.termsChecked = false;
              this.showTermsDialog = true;
            }
          },
          error: () => {}
        });
      },
      error: () => {}
    });
  }

  // Flag "Exigir novo aceite" = sim: recusar desloga (não pode usar sem aceitar).
  recusarTermos(): void {
    StorageUtil.logout();
    this.router.navigate(['/login']);
  }

  // Flag = não: cancelar apenas fecha a modal (sem deslogar), como era antes.
  cancelarTermos(): void {
    this.showTermsDialog = false;
  }

  acceptMarinaTerms(): void {
    this.acceptingTerms = true;
    this.marinaTermUseService.save({ type: TermUseType.MARINA_COMMERCIAL_TERMS }).subscribe({
      next: () => {
        this.acceptingTerms = false;
        this.showTermsDialog = false;
      },
      error: () => {
        this.acceptingTerms = false;
        this.toasty.error('Não foi possível registrar o aceite. Tente novamente.');
      }
    });
  }

  // Termo cadastrado como texto (rich text): gera o PDF a partir do HTML no próprio navegador.
  baixarTermoPdf(): void {
    if (!this.termsContent) {
      return;
    }
    const container = document.createElement('div');
    container.innerHTML = this.termsContent;
    container.style.padding = '16px';
    html2pdf().set({
      margin: 10,
      filename: `termo-uso-v${this.termsVersion}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(container).save();
  }

  openWhatsappSupport(): void {
    const url = `https://wa.me/${DefaultComponent.WHATSAPP_SUPPORT_PHONE}`
      + `?text=${encodeURIComponent(DefaultComponent.WHATSAPP_SUPPORT_MESSAGE)}`;
    window.open(url, '_blank');
  }
}
